import { Any } from 'cosmjs-types/google/protobuf/any'

import { VoteOption } from '@/types/governance'

import { encodeInt64, encodeString } from './proto'

export interface VoteParams {
  proposal_id: string
  voter: string
  option: VoteOption
  metadata?: string
}

export interface WeightedVoteParams {
  proposal_id: string
  voter: string
  options: Array<{
    option: VoteOption
    weight: string
  }>
  metadata?: string
}

function voteOptionToInt(option: VoteOption): number {
  const optionMap: Record<VoteOption, number> = {
    [VoteOption.Unspecified]: 0,
    [VoteOption.Yes]: 1,
    [VoteOption.Abstain]: 2,
    [VoteOption.No]: 3,
    [VoteOption.NoWithVeto]: 4,
  }
  return optionMap[option]
}

export function buildVoteMsg(params: VoteParams): Any {
  const proposalId = BigInt(params.proposal_id)
  const optionInt = voteOptionToInt(params.option)

  const value = Uint8Array.from([
    ...encodeInt64(1, proposalId),
    ...encodeString(2, params.voter),
    ...encodeInt64(3, BigInt(optionInt)),
    ...encodeString(4, params.metadata ?? ''),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.gov.v1.MsgVote',
    value,
  })
}

export function buildWeightedVoteMsg(params: WeightedVoteParams): Any {
  const proposalId = BigInt(params.proposal_id)

  const optionsBytes = params.options.flatMap(opt => {
    const optionInt = voteOptionToInt(opt.option)
    const optionBytes = Uint8Array.from([
      ...encodeInt64(1, BigInt(optionInt)),
      ...encodeString(2, opt.weight),
    ])
    // Encode as message field number 3
    const tag = Uint8Array.from([(3 << 3) | 2])
    const len = BigInt(optionBytes.length)
    const lenVarint: number[] = []
    let v = len
    while (v >= 0x80n) {
      lenVarint.push(Number((v & 0x7fn) | 0x80n))
      v >>= 7n
    }
    lenVarint.push(Number(v))
    return [...tag, ...lenVarint, ...optionBytes]
  })

  const value = Uint8Array.from([
    ...encodeInt64(1, proposalId),
    ...encodeString(2, params.voter),
    ...optionsBytes,
    ...encodeString(4, params.metadata ?? ''),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.gov.v1.MsgVoteWeighted',
    value,
  })
}
